// import external dependencies
import * as THREE from 'three';
import GLTFLoader from 'three-gltf-loader';
import OrbitControls from 'three-orbitcontrols';

export let elements = {};

// glTF texture types. `envMap` is deliberately omitted, as it's used internally
// by the loader but not part of the glTF format.
export const MAP_NAMES = [
  'map',
  'aoMap',
  'emissiveMap',
  'glossinessMap',
  'metalnessMap',
  'normalMap',
  'roughnessMap',
  'specularMap',
];

export function setup(container) {
  elements.container = container;

  const threeWidth = 1400;
  const threeHeight = 760;

  elements.scene = new THREE.Scene();
  elements.scene.background = new THREE.Color( 0xf7f7f7 );

  elements.camera = new THREE.PerspectiveCamera( 60, threeWidth / threeHeight, 0.01, 1000 );
  elements.scene.add(elements.camera);

  elements.lights = [];
  elements.lighting = {
    // Lights
    addLights: true,
    exposure: 0.8,
    textureEncoding: 'sRGB',
    ambientIntensity: 0.55,
    ambientColor: 0xFFFFFF,
    directIntensity: 0.95,
    directColor: 0xFFFFFF,
    bgColor1: '#ffffff',
    bgColor2: '#353535',
  };

  // Camera 1 x: -3 / y: 43 / z: -54
  // Camera 2 x: -2 / y: 18 / z: -44
  elements.cameras = [
    {
      x: 12,
      y: 46,
      z: -66,
    }, {
      x: 12,
      y: 42,
      z: -44,
    },
  ];

  // new THREE.WebGLRenderer( { alpha: true, antialias: true } );
  elements.renderer = new THREE.WebGLRenderer( { antialias: true } );
  elements.renderer.setSize( threeWidth, threeHeight );
  setControls();

  return elements.renderer;
}

export function logCamera() {
  console.log(elements.camera);
}

export function loadGLTF (asset, cameraID) {
  let loader = new GLTFLoader();
  loader.load(
    asset,
    (gltf) => {
      // called when the resource is loaded
      elements.model = gltf;
      elements.content = gltf.scene;
      // gltf.scene.scale.set( 2, 2, 2 );
      gltf.scene.position.x = 0;
      gltf.scene.position.y = 0;
      gltf.scene.position.z = 0;
      elements.scene.add(gltf.scene);

      let position = (cameraID) ? elements.cameras[cameraID] : elements.cameras[0];
      elements.cameraPosition = position;
      elements.updateCamera = true;
      setCamera(gltf.scene);
      updateLights();
      updateTextureEncoding(gltf.scene);
      elements.controls.update();
      // elements.controls.minPolarAngle = elements.controls.maxPolarAngle = elements.controls.getPolarAngle();

      animate();
    }, undefined, (error) => {
      // called when loading has errors
      console.error('Error loading GLTF', error);
    }
  );
  // (xhr) => {
  //   // called while loading is progressing
  //   console.log(`${( xhr.loaded / xhr.total * 100 )}% loaded`);
  // },
}

export function clearScene () {
  if ( !elements.content ) return;

  elements.scene.remove( elements.content );

  // dispose geometry
  elements.content.traverse((node) => {
    if ( !node.isMesh ) return;

    node.geometry.dispose();
  } );

  // dispose textures
  traverseMaterials( elements.content, (material) => {
    MAP_NAMES.forEach( (map) => {
      if (material[ map ]) material[ map ].dispose();
    } );
  } );
}

export function setCamera (object) {
  object.updateMatrixWorld();
  const box = new THREE.Box3().setFromObject(object);
  const size = box.getSize(new THREE.Vector3()).length();
  const center = box.getCenter(new THREE.Vector3());

  elements.controls.reset();

  object.position.x += (object.position.x - center.x);
  object.position.y += (object.position.y - center.y);
  object.position.z += (object.position.z - center.z);
  elements.controls.maxDistance = size * 10;
  elements.camera.near = size / 100;
  elements.camera.far = size * 100;
  elements.camera.updateProjectionMatrix();

  // GLTF viewer defaults
  // position.x += size / 2.0;
  // position.y += size / 5.0;
  // position.z += size / 2.0;
  // LookAt viewer default
  // x: 30 / y: 20 / z: -65
  elements.camera.position.x = 30;
  elements.camera.position.y = 20;
  elements.camera.position.z = -65;
  elements.camera.lookAt(center);
  elements.cameraTarget = center;

  // elements.controls.saveState();
}

export function setControls () {
  if (elements.controls) {
    elements.controls.dispose();
  }

  const controls = new OrbitControls(elements.camera, elements.renderer.domElement);
  controls.autoRotate = false;
  controls.autoRotateSpeed = -10;
  controls.screenSpacePanning = true;
  controls.enableZoom = false;
  controls.enabled = false;
  elements.controls = controls;
}

export function updateTextureEncoding (object) {
  const encoding = THREE.sRGBEncoding;
  traverseMaterials(object, (material) => {
    if (material.map) material.map.encoding = encoding;
    if (material.emissiveMap) material.emissiveMap.encoding = encoding;
    if (material.map || material.emissiveMap) material.needsUpdate = true;
  });
}

export function traverseMaterials (object, callback) {
  object.traverse((node) => {
    if (!node.isMesh) return;
    const materials = Array.isArray(node.material)
      ? node.material
      : [node.material];
    materials.forEach(callback);
  });
}

export function updateLights () {
  if (elements.lights.length > 0) {
    removeLights();
  }

  const light1  = new THREE.AmbientLight(elements.lighting.ambientColor, elements.lighting.ambientIntensity);
  light1.name = 'ambient_light';
  elements.camera.add( light1 );

  const light2  = new THREE.DirectionalLight(elements.lighting.directColor, elements.lighting.directIntensity);
  light2.position.set(0.5, 0, 0.866); // ~60º
  light2.name = 'main_light';
  elements.camera.add( light2 );

  elements.lights.push(light1, light2);

  elements.renderer.toneMappingExposure = elements.lighting.exposure;

  if (elements.lights.length === 2) {
    elements.lights[0].intensity = elements.lighting.ambientIntensity;
    elements.lights[0].color.setHex(elements.lighting.ambientColor);
    elements.lights[1].intensity = elements.lighting.directIntensity;
    elements.lights[1].color.setHex(elements.lighting.directColor);
  }
}

export function addGUI () {
  // Lighting controls.
  const lightFolder = elements.gui.addFolder('Lighting');
  const encodingCtrl = lightFolder.add(elements.lighting, 'textureEncoding', ['sRGB', 'Linear']);
  encodingCtrl.onChange(() => updateTextureEncoding());
  lightFolder.add(elements.renderer, 'gammaOutput').onChange(() => {
    traverseMaterials(elements.content, (material) => {
      material.needsUpdate = true;
    });
  });
  [
    lightFolder.add(elements.lighting, 'exposure', 0, 2),
    lightFolder.add(elements.lighting, 'addLights').listen(),
    lightFolder.add(elements.lighting, 'ambientIntensity', 0, 2),
    lightFolder.addColor(elements.lighting, 'ambientColor'),
    lightFolder.add(elements.lighting, 'directIntensity', 0, 4),
    lightFolder.addColor(elements.lighting, 'directColor'),
  ].forEach((ctrl) => ctrl.onChange(() => updateLights()));

  // Camera controls.
  const cameraFolder = elements.gui.addFolder('Camera');
  [
    cameraFolder.add(elements.camera.position, 'x').listen(),
    cameraFolder.add(elements.camera.position, 'y').listen(),
    cameraFolder.add(elements.camera.position, 'z').listen(),
  ].forEach((ctrl) => ctrl.onChange(() => {
    elements.camera.updateProjectionMatrix();
    elements.controls.saveState();
  }));

  const guiWrap = document.createElement('div');
  elements.container.appendChild( guiWrap );
  guiWrap.classList.add('interactive-3d__gui');
  guiWrap.appendChild(elements.gui.domElement);
  elements.gui.open();
}

export function removeLights () {
  elements.lights.forEach((light) => light.parent.remove(light));
  elements.lights.length = 0;
}

export function animate () {
  requestAnimationFrame(animate);

  elements.renderer.render(elements.scene, elements.camera);

  if (elements.updateCamera) {
    elements.updateCamera = false;

    elements.camera.position.x = elements.cameraPosition.x;
    elements.camera.position.y = elements.cameraPosition.y;
    elements.camera.position.z = elements.cameraPosition.z;
    // elements.controls.update();
    elements.controls.saveState();
  }
}

export function changeScene (asset, cameraID) {
  if (asset) {
    clearScene();
    loadGLTF(asset, cameraID);
  }
}
